import type { AppHeaderApp } from "./AppHeader/types";

/**
 * Grid da página de cada aplicação — a caixa do "paper" branco central.
 *
 * `AppHeader` e `AppFooter` usam isto para alinhar o conteúdo com o paper: a
 * faixa colorida atravessa a viewport e o conteúdo fica contido e centralizado
 * pela **mesma** regra que centraliza o paper (`max-width` +
 * `margin-inline: auto`). Assim os dois alinham em qualquer largura, sem
 * ninguém casar números.
 *
 * **Passe a mesma constante no paper da aplicação:**
 *
 * ```tsx
 * import { APP_CONTENT_GRID } from "@cub-com-br/theme/app-header";
 *
 * const grid = APP_CONTENT_GRID["statement-viewer"];
 * <Paper sx={{ maxWidth: grid.maxWidth, mx: "auto", px: `${grid.gutter}px` }} />
 * ```
 *
 * Com header, paper e footer lendo o mesmo valor, o alinhamento é **por
 * construção**: mexer aqui move os três, e não há número duplicado para
 * divergir. É a razão de o grid viver no DS e não na aplicação.
 *
 * Valores medidos no DOM em produção, em 6 larguras cada.
 */
export interface AppContentGrid {
  /** Largura máxima do paper, em px. Acima dela o paper centraliza. */
  maxWidth: number;
  /** Respiro lateral do conteúdo no desktop, em px. */
  gutter: number;
  /** Respiro lateral do conteúdo no mobile, em px. */
  gutterMobile: number;
}

/**
 * Um `Record` exaustivo de propósito: app novo no eixo não compila sem declarar
 * seu grid — mesma disciplina de `APP_HEADER_TITLES`. Sem isso, um valor novo
 * herdaria silenciosamente a geometria de outra aplicação.
 *
 * ⚠️ **`onboarding-app` é decisão, não medida.** As outras duas entradas foram
 * medidas no DOM em produção; a revisão de dados **não organiza o conteúdo num
 * paper com essa regra**, então aqui não há paper com que alinhar. Adotar a
 * geometria do `statement-viewer` é escolha de consistência: a revisão leva ao
 * extrato, e o header/footer não devem trocar de largura no meio da jornada.
 *
 * Consequência prática: medir a página do onboarding e ver que não bate **não é
 * bug**. Se algum dia ela ganhar um paper próprio, é aqui que o número muda.
 */
export const APP_CONTENT_GRID: Record<AppHeaderApp, AppContentGrid> = {
  // Área do Cliente: paper largo, e o respiro aperta no mobile.
  "statement-viewer": { maxWidth: 1176, gutter: 24, gutterMobile: 16 },
  // Visualizador de Cobranças: paper estreito (é um boleto), respiro constante.
  "billing-viewer": { maxWidth: 838, gutter: 24, gutterMobile: 24 },
  // Espelha o statement por decisão de consistência de jornada — ver acima.
  "onboarding-app": { maxWidth: 1176, gutter: 24, gutterMobile: 16 },
};
